import React, { useState } from "react";
import { useGetAllDeals, useDeleteDeal } from "./hooks";
import { Deal } from "./api";
import { toast } from "react-toastify";
import { useAuth } from "../../auth/AuthContext";
import { useI18n } from "../../i18n";

interface DealsTableProps {
  onEditDeal?: (deal: Deal) => void;
}

const DealsTable: React.FC<DealsTableProps> = ({ onEditDeal }) => {
  const { t, code } = useI18n();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [filterOpen, setFilterOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Filter criteria
  const [regionFilter, setRegionFilter] = useState("");
  const [servicesFilter, setServicesFilter] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Sorting
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const itemsPerPage = 10;

  const { user } = useAuth();
  const companyVatId = user?.companyVatId;

  const { data: dealsResponse, isLoading, error } = useGetAllDeals(companyVatId);
  const deleteDealMutation = useDeleteDeal();

  // Extract deals from response
  const deals = dealsResponse?.data || [];

  // Handle dropdown toggle with position calculation
  const toggleDropdown = (
    dealId: string,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (activeDropdown === dealId) {
      setActiveDropdown(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.right + window.scrollX - 192, // 192px = w-48
      });
      setActiveDropdown(dealId);
    }
    setDeleteConfirm(null);
  };

  // Handle edit deal
  const handleEditDeal = (deal: Deal) => {
    if (onEditDeal) {
      onEditDeal(deal);
    }
    setActiveDropdown(null);
  };

  // Handle delete deal
  const handleDeleteDeal = async (dealId: string) => {
    try {
      await deleteDealMutation.mutateAsync(dealId);
      toast.success(t("deals.toast.deleted"));
    } catch (error) {
      toast.error(t("deals.toast.deleteFailed"));
    }
    setDeleteConfirm(null);
    setActiveDropdown(null);
  };

  // Close row menus / the export dropdown when clicking outside.
  // The inline filter bar stays open — it's toggled only by the Filter button.
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      setActiveDropdown(null);
      setDeleteConfirm(null);
      setExportOpen(false);
    };

    if (activeDropdown || deleteConfirm || exportOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeDropdown, deleteConfirm, exportOpen]);

  // Distinct region / service values for the filter popover (derived from data)
  const regionOptions = React.useMemo(
    () =>
      Array.from(
        new Set(deals.map((d: any) => d.region).filter(Boolean))
      ).sort() as string[],
    [deals]
  );
  const serviceOptions = React.useMemo(
    () =>
      Array.from(
        new Set(deals.flatMap((d: any) => d.services || []).filter(Boolean))
      ).sort() as string[],
    [deals]
  );

  const activeFilterCount =
    (regionFilter ? 1 : 0) +
    (servicesFilter.length > 0 ? 1 : 0) +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0);

  const clearFilters = () => {
    setRegionFilter("");
    setServicesFilter([]);
    setDateFrom("");
    setDateTo("");
  };

  const toggleServiceFilter = (service: string) => {
    setServicesFilter((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
  };

  // Filter deals based on search term + popover filters
  const filteredDeals = deals.filter((deal: any) => {
    // Search term (matches across several fields)
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !term ||
      deal.companyName?.toLowerCase().includes(term) ||
      deal.vatNumber?.toLowerCase().includes(term) ||
      deal.contactPerson?.toLowerCase().includes(term) ||
      deal.region?.toLowerCase().includes(term) ||
      deal.services?.some((service: string) =>
        service.toLowerCase().includes(term)
      );

    // Region filter
    const matchesRegion = !regionFilter || deal.region === regionFilter;

    // Services filter — deal must include every selected service
    const matchesServices =
      servicesFilter.length === 0 ||
      servicesFilter.every((s) => deal.services?.includes(s));

    // Created date range
    const created = deal.createdAt || deal.created_at;
    const matchesFrom =
      !dateFrom || (created && new Date(created) >= new Date(dateFrom));
    const matchesTo =
      !dateTo ||
      (created && new Date(created) <= new Date(dateTo + "T23:59:59"));

    return (
      matchesSearch &&
      matchesRegion &&
      matchesServices &&
      matchesFrom &&
      matchesTo
    );
  });

  // Build and download a CSV from a set of deals
  const exportToCsv = (rows: any[], filename: string) => {
    const headers = [
      t("deals.export.companyName"),
      t("deals.export.vatNumber"),
      t("deals.export.contactPerson"),
      t("deals.export.region"),
      t("deals.export.services"),
      t("deals.export.note"),
      t("deals.export.createdDate"),
      t("deals.export.expirationDate"),
    ];
    const escape = (value: any) => {
      const str = value == null ? "" : String(value);
      // Quote when the value contains a comma, quote, or newline
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [
      headers.join(","),
      ...rows.map((deal) => {
        const expiry = deal.expiresAt
          ? new Date(deal.expiresAt)
          : getExpiryDate(deal.createdAt || deal.created_at);
        return [
          deal.companyName,
          deal.vatNumber,
          deal.contactPerson,
          deal.region,
          (deal.services || []).join("; "),
          deal.note,
          formatDate(deal.createdAt || deal.created_at),
          expiry ? formatDate(expiry.toISOString()) : "-",
        ]
          .map(escape)
          .join(",");
      }),
    ];
    const blob = new Blob(["﻿" + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const todayStamp = new Date().toISOString().slice(0, 10);

  const handleExportAll = () => {
    if (deals.length === 0) {
      toast.info(t("deals.export.nothingToExport"));
    } else {
      exportToCsv(deals, `deals-all-${todayStamp}.csv`);
    }
    setExportOpen(false);
  };

  const handleExportFiltered = () => {
    if (filteredDeals.length === 0) {
      toast.info(t("deals.export.nothingToExport"));
    } else {
      exportToCsv(filteredDeals, `deals-filtered-${todayStamp}.csv`);
    }
    setExportOpen(false);
  };

  // Reservation policy: a deal is reserved for 6 months from its creation date
  const RESERVATION_MONTHS = 6;
  const getExpiryDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return null;
    d.setMonth(d.getMonth() + RESERVATION_MONTHS);
    return d;
  };

  // Click a column header to sort; same column toggles asc/desc
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // Comparable value per sort key
  const sortValue = (deal: any, key: string) => {
    switch (key) {
      case "services":
        return (deal.services || []).join(", ").toLowerCase();
      case "createdAt":
        return new Date(deal.createdAt || deal.created_at || 0).getTime();
      case "expiresAt": {
        const expiry = deal.expiresAt
          ? new Date(deal.expiresAt)
          : getExpiryDate(deal.createdAt || deal.created_at);
        return expiry ? expiry.getTime() : 0;
      }
      default:
        return (deal[key] ?? "").toString().toLowerCase();
    }
  };

  // Apply sorting to the filtered list
  const sortedDeals = React.useMemo(() => {
    if (!sortKey) return filteredDeals;
    const sorted = [...filteredDeals].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDeals, sortKey, sortDir]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedDeals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDeals = sortedDeals.slice(startIndex, endIndex);

  // Reset to first page when search, filters, or sort change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, regionFilter, servicesFilter, dateFrom, dateTo, sortKey, sortDir]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString(code, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Sortable column header with directional arrows
  const SortHeader = ({
    label,
    sortField,
    align = "left",
  }: {
    label: string;
    sortField: string;
    align?: "left" | "center";
  }) => {
    const active = sortKey === sortField;
    return (
      <th
        className={`px-4 py-3 font-semibold select-none cursor-pointer group/th ${
          align === "center" ? "text-center" : "text-left"
        }`}
        onClick={() => handleSort(sortField)}
      >
        <span
          className={`inline-flex items-center gap-1.5 ${
            align === "center" ? "justify-center" : ""
          } ${active ? "text-gray-700" : ""}`}
        >
          {label}
          <span className="inline-flex flex-col leading-none text-[9px]">
            <i
              className={`fa-solid fa-caret-up -mb-0.5 ${
                active && sortDir === "asc"
                  ? "text-blue-600"
                  : "text-gray-300 group-hover/th:text-gray-400"
              }`}
            ></i>
            <i
              className={`fa-solid fa-caret-down ${
                active && sortDir === "desc"
                  ? "text-blue-600"
                  : "text-gray-300 group-hover/th:text-gray-400"
              }`}
            ></i>
          </span>
        </span>
      </th>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 mt-2 flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">{t("deals.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 mt-2 flex-1 flex items-center justify-center">
        <div className="text-center">
          <i className="fa-solid fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
          <p className="text-red-600 mb-2">{t("deals.loadFailed")}</p>
          <p className="text-gray-500 text-sm">
            {t("deals.refreshHint")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 p-3 mt-2 flex flex-col">
        {/* Search and actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
          <div className="relative w-full md:w-72">
            <input
              type="text"
              placeholder={t("deals.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm"
            />
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          </div>
          <div className="flex gap-2 justify-end">
            {/* Filter toggle */}
            <button
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                filterOpen || activeFilterCount > 0
                  ? "border-blue-300 bg-blue-50 text-blue-700"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setFilterOpen((o) => !o);
                setExportOpen(false);
              }}
            >
              <i className="fa-solid fa-filter"></i> {t("deals.filterButton")}
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-blue-600 text-white text-xs font-semibold">
                  {activeFilterCount}
                </span>
              )}
              <i
                className={`fa-solid fa-chevron-down text-xs transition-transform ${
                  filterOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {/* Export */}
            <div className="relative">
              <button
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium"
                onClick={(e) => {
                  e.stopPropagation();
                  setExportOpen((o) => !o);
                  setFilterOpen(false);
                }}
              >
                <i className="fa-solid fa-file-export"></i> {t("common.export")}
                <i className="fa-solid fa-chevron-down text-xs"></i>
              </button>

              {exportOpen && (
                <div
                  className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={handleExportAll}
                  >
                    <span>
                      <i className="fa-solid fa-database mr-2 text-gray-400"></i>
                      {t("common.exportAll")}
                    </span>
                    <span className="text-xs text-gray-400">{deals.length}</span>
                  </button>
                  <button
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={handleExportFiltered}
                  >
                    <span>
                      <i className="fa-solid fa-filter mr-2 text-gray-400"></i>
                      {t("common.exportFiltered")}
                    </span>
                    <span className="text-xs text-gray-400">
                      {filteredDeals.length}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Inline filter panel (toggled by the Filter button) */}
        {filterOpen && (
          <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3.5">
            {/* Panel header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <i className="fa-solid fa-sliders text-gray-400"></i>
                {t("deals.filters.title")}
              </div>
              {activeFilterCount > 0 && (
                <button
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-red-600 transition-colors"
                  onClick={clearFilters}
                >
                  <i className="fa-solid fa-xmark"></i>
                  {t("deals.filters.clearAll")}
                </button>
              )}
            </div>

            {/* Fields row — aligned on a common baseline */}
            <div className="flex flex-wrap items-end gap-x-5 gap-y-4">
              {/* Region */}
              <div className="flex flex-col min-w-[180px]">
                <label className="text-xs font-medium text-gray-500 mb-1.5">
                  {t("deals.filters.region")}
                </label>
                <select
                  className="h-9 border border-gray-300 rounded-lg px-3 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                  value={regionFilter}
                  onChange={(e) => setRegionFilter(e.target.value)}
                >
                  <option value="">{t("deals.filters.allRegions")}</option>
                  {regionOptions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>

              {/* Created date */}
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-500 mb-1.5">
                  {t("deals.filters.createdDate")}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={dateFrom}
                    max={dateTo || undefined}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9 w-[150px] border border-gray-300 rounded-lg px-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  <span className="text-gray-400 text-sm">→</span>
                  <input
                    type="date"
                    value={dateTo}
                    min={dateFrom || undefined}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-9 w-[150px] border border-gray-300 rounded-lg px-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              </div>

              {/* Services — spans full width on its own line when wrapped */}
              <div className="flex flex-col flex-1 min-w-[240px]">
                <label className="text-xs font-medium text-gray-500 mb-1.5">
                  {t("deals.filters.services")}
                </label>
                {serviceOptions.length === 0 ? (
                  <p className="text-xs text-gray-400 h-9 flex items-center">
                    {t("deals.filters.noServices")}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 min-h-9 items-center">
                    {serviceOptions.map((service) => {
                      const selected = servicesFilter.includes(service);
                      return (
                        <button
                          key={service}
                          type="button"
                          onClick={() => toggleServiceFilter(service)}
                          className={`px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            selected
                              ? "bg-teal-600 text-white border-teal-600"
                              : "bg-white text-gray-600 border-gray-300 hover:border-teal-400 hover:text-teal-700"
                          }`}
                        >
                          {selected && <i className="fa-solid fa-check mr-1"></i>}
                          {service}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Table - Dynamic height based on content */}
        {currentDeals.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <i className="fa-solid fa-handshake text-gray-300 text-6xl mb-6"></i>
              <p className="text-xl font-medium text-gray-600 mb-3">
                {searchTerm ? t("deals.empty.noResultsTitle") : t("deals.empty.emptyTitle")}
              </p>
              <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
                {searchTerm
                  ? t("deals.empty.noResultsBody")
                  : t("deals.empty.emptyBody")}
              </p>
              {!searchTerm && (
                <button className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                  <i className="fa-solid fa-plus mr-2"></i>
                  {t("deals.createFirstDeal")}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden relative">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-gray-500 font-semibold border-b bg-gray-50/50 text-xs">
                    <th className="px-4 py-3 text-left w-12">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <SortHeader label={t("deals.table.companyName")} sortField="companyName" />
                    <SortHeader label={t("deals.table.vatNumber")} sortField="vatNumber" />
                    <SortHeader label={t("deals.table.contactPerson")} sortField="contactPerson" />
                    <SortHeader label={t("deals.table.region")} sortField="region" />
                    <SortHeader label={t("deals.table.services")} sortField="services" />
                    <SortHeader label={t("deals.table.createdDate")} sortField="createdAt" />
                    <SortHeader label={t("deals.table.expirationDate")} sortField="expiresAt" />
                    <th className="px-4 py-3 text-center w-20 font-semibold">
                      {t("deals.table.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {currentDeals.map((deal: any, index: number) => (
                    <tr
                      key={deal.uuid}
                      className="border-b border-gray-100 hover:bg-blue-50/30 transition-colors group last:border-b-0"
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-4 font-medium text-gray-900">
                        {deal.companyName || "-"}
                      </td>
                      <td className="px-4 py-4 text-gray-600 font-mono text-sm">
                        {deal.vatNumber || "-"}
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        <div className="flex items-center">
                          <i className="fa-solid fa-user text-gray-400 text-xs mr-2"></i>
                          {deal.contactPerson || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {deal.region ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
                            <i className="fa-solid fa-map-marker-alt mr-1 text-xs"></i>
                            {deal.region}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {deal.services && deal.services.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {deal.services
                              .slice(0, 2)
                              .map((service: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-1 rounded-md bg-teal-50 text-teal-700 text-xs font-medium border border-teal-200"
                                >
                                  {service}
                                </span>
                              ))}
                            {deal.services.length > 2 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-50 text-gray-600 text-xs font-medium border border-gray-200 relative group/tooltip">
                                +{deal.services.length - 2}
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                  {deal.services.slice(2).join(", ")}
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">{t("deals.noServices")}</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-gray-600 text-sm">
                        <div className="flex items-center">
                          <i className="fa-regular fa-calendar text-gray-400 text-xs mr-2"></i>
                          {formatDate(deal.createdAt || deal.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {(() => {
                          // Prefer a backend-provided expiry; fall back to created + 6 months
                          const expiry = deal.expiresAt
                            ? new Date(deal.expiresAt)
                            : getExpiryDate(deal.createdAt || deal.created_at);
                          if (!expiry || isNaN(expiry.getTime())) {
                            return <span className="text-gray-400">-</span>;
                          }
                          const now = new Date();
                          const msLeft = expiry.getTime() - now.getTime();
                          const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
                          const expired = daysLeft < 0;
                          const soon = !expired && daysLeft <= 30;
                          const color = expired
                            ? "bg-red-50 text-red-700 border-red-200"
                            : soon
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-gray-50 text-gray-600 border-gray-200";
                          return (
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${color}`}
                              title={
                                expired
                                  ? t("deals.expiry.expired", { days: Math.abs(daysLeft) })
                                  : t("deals.expiry.daysLeft", { days: daysLeft })
                              }
                            >
                              <i
                                className={`fa-solid ${
                                  expired
                                    ? "fa-circle-exclamation"
                                    : soon
                                    ? "fa-clock"
                                    : "fa-hourglass-half"
                                } text-[10px]`}
                              ></i>
                              {formatDate(expiry.toISOString())}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors group-hover:bg-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(deal.uuid, e);
                          }}
                        >
                          <i className="fa-solid fa-ellipsis-vertical"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {currentDeals.length > 0 && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 text-sm text-gray-600">
            <span className="font-medium">
              {t("deals.showing", {
                from: startIndex + 1,
                to: Math.min(endIndex, filteredDeals.length),
                total: filteredDeals.length,
              })}
              {searchTerm && (
                <span className="ml-2 text-gray-400">
                  {t("common.filteredFromTotal", { total: deals.length })}
                </span>
              )}
            </span>
            <div className="flex items-center gap-1">
              <button
                className="w-7 h-7 flex items-center justify-center rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-50"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                {"<"}
              </button>
              <button className="w-7 h-7 flex items-center justify-center rounded bg-gray-900 text-white font-bold">
                {currentPage}
              </button>
              <button
                className="w-7 h-7 flex items-center justify-center rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-50"
                disabled={currentPage === totalPages || totalPages === 1}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                {">"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Fixed position dropdown menu */}
      {activeDropdown && (
        <div
          className="fixed inset-0 z-[9999]"
          onClick={() => setActiveDropdown(null)}
        >
          <div
            className="absolute w-48 bg-white border border-gray-200 rounded-lg shadow-xl"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="py-1">
              <button
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => {
                  const deal = deals.find(
                    (d: Deal) => d.uuid === activeDropdown
                  );
                  if (deal) handleEditDeal(deal);
                }}
              >
                <i className="fa-solid fa-edit mr-3 text-blue-500"></i>
                {t("deals.rowMenu.edit")}
              </button>
              <hr className="my-1 border-gray-100" />
              <button
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                onClick={() => {
                  setDeleteConfirm(activeDropdown);
                  setActiveDropdown(null);
                }}
              >
                <i className="fa-solid fa-trash mr-3"></i>
                {t("deals.rowMenu.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed position delete confirmation */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 z-[9999]"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="absolute w-64 bg-white border border-gray-200 rounded-lg shadow-xl"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4">
              <div className="flex items-center mb-3">
                <i className="fa-solid fa-exclamation-triangle text-red-500 mr-2"></i>
                <span className="font-medium text-gray-900">
                  {t("deals.confirmDelete.title")}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {t("deals.confirmDelete.body", {
                  name:
                    deals.find((d: Deal) => d.uuid === deleteConfirm)
                      ?.companyName ?? "",
                })}
              </p>
              <div className="flex gap-2">
                <button
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                  onClick={() => handleDeleteDeal(deleteConfirm)}
                  disabled={deleteDealMutation.isPending}
                >
                  {deleteDealMutation.isPending ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                      {t("deals.confirmDelete.submitting")}
                    </>
                  ) : (
                    t("common.delete")
                  )}
                </button>
                <button
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
                  onClick={() => setDeleteConfirm(null)}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DealsTable;
